import { Injectable } from 'angular-ts-decorators';

import type { IConfig } from './config';

/**
 * # domain
 * Service in the sharedModule.
 * It takes care of figuring out proper domain for different purposes.
 */
@Injectable('domain')
export class Domain {
  /**
   * @deprecated
   * Alias for {@link sharedModule.domain#createSibling createSibling}
   */
  public sibling: typeof Domain.prototype.createSibling;

  constructor(
    private $location: ng.ILocationService,
    private config: IConfig,
  ) {
    'ngInject';

    this.sibling = this.createSibling;
  }

  /**
   * Get main application domain based on the current. For x.y.nl returns y.nl.
   * @return {string}
   * parent domain
   */
  getParent(): string {
    // TODO consider caching the value
    return this.$location.host().split('.').splice(1).join('.');
  }

  /**
   * Gets the lowest level of the current domain
   * @return
   * sud-domain
   */
  getSubdomain(): string {
    // TODO consider caching the value
    return this.$location.host().split('.')[0];
  }

  /**
   * Gets the current app
   * @return
   * sud-domain
   */
  getApp(): string {
    const match = this.$location.absUrl().match(/([^/]*)\.html#/);

    if (match) {
      return match[1];
    }
    return undefined;
  }

  /**
   * Get request ready spynl domain
   * @return
   * spynl domain
   */
  getSpynl(): string {
    return '//' + this.createSibling(this.config.spynlSubdomain);
  }

  /**
   * Get request ready spynl service domain
   * @return {string}
   * spynl service domain
   */
  getSpynlService(): string {
    return '//' + this.sibling(this.config.spynlServices);
  }

  /**
   * Creats sybling domain - replace last level domain with sibling name. When
   * run at http://docs.dev.swcloud.nl/ with spynl as argument will return
   * **spynl.dev.swcloud.nl**.
   * @param {string} name
   * sibling name
   * @param {boolean} includeProtocol
   * flag set to true if we need sybling with protocol url part included
   * @return {string}
   * sibling domain with the protocol (if it's a case)
   */
  createSibling(name: string, includeProtocol?: boolean): string {
    const prefix = includeProtocol ? this.$location.protocol() + '://' : '';
    return prefix + name + '.' + this.getParent();
  }

  /**
   * Checks if provided url is a sibling domain
   * @param {string} url
   * route to be checked
   * @return {boolean}
   * True if on sibling, false otherwise
   */
  isSibling(url: string): boolean {
    const parentDomain = this.getParent();

    try {
      // we don't care about right protocol, we just want relative url to be
      // parsed in some way
      const parsedUrl = new URL(url, 'http://example.com');

      return parsedUrl.host.endsWith(parentDomain);
    } catch (error) {
      return false;
    }
  }

  /**
   * another service. This is useful, since we operate on
   * many different base domains. So we need some way to
   * determine that domain.
   * @param {string} destination – The destination specification.
   *   This is made up of one or two components, split by a hash or /
   *   sign ('# | /'); The first is the subdomain we're
   *   redirecting two (gate, core, dashboard, ...); the
   *   second is optional and is the route inside the other module.
   *   Based on the divider # or / it will route to an angular routing if it's an #
   *   And it will route to a normal route if it's divided by a /
   * @return {string}
   * calculate a deep link to a sibling domain
   */
  link(destination: string): string {
    if (!angular.isString(destination)) {
      throw new Error('destination should be a string');
    }

    if (destination.length === 0) {
      throw new Error('destination should have no-zero length');
    }

    if (
      destination.startsWith('http://') ||
      destination.startsWith('https://')
    ) {
      return destination;
    }

    // regex allows <any letters, number or _>[#<any chars>]
    const match = destination.match(/^([^#\/\n@]+)(?:([#\/])(.*))?$/);
    if (match.length !== 4) {
      throw new Error(
        'Invalid inter-domain link ' +
          'specification "' +
          destination +
          '". Links must have ' +
          'the form "$subdomain[/$path#$hashPath]", where subdomain ' +
          'is the service you are linking to (e.g. gate, ' +
          'dashboard, …) and $path is the route on that ' +
          'service, and $hashPath is deep link in the application',
      );
    }

    const routingType = match[2] || '#';
    const domain = match[1];
    const path = match[3] || '';
    let url = '';

    if (!path) {
      url = routingType === '#' ? '#/' : '/';
    } else {
      url = (routingType === '#' ? '#' : '/') + path;
    }

    if (domain === 'softwear6' || domain === 'spynl-services') {
      return this.sibling(domain, true) + url;
    }

    return this.sibling('www', true) + `/${domain}.html${url}`;
  }

  /**
   * Returns the given link with the current subdomain injected, if appropriate.
   * Only applies for qliqpoint.fashion, not for latestcollection.fashion.
   * Handles localhost as a special case.
   * @param {string} link - The link to modify
   * @param {string} [currentUrl] - The current URL (defaults to $location.absUrl())
   * @return {string} - The modified link
   */
  withCurrentSubdomain(link: string, currentUrl?: string): string {
    const url: string = currentUrl || this.$location.absUrl();
    const match = url.match(/\/\/([a-z0-9-]+)\./i);
    let subdomain: string = '';
    if (match) {
      if (match[1] === 'www') {
        // Try to extract the next part after www
        const afterWww = url.match(/\/\/www\.([a-z0-9-]+)\./i);
        subdomain = afterWww ? afterWww[1] : '';
      } else {
        subdomain = match[1];
      }
    }

    // Only apply if currentUrl is qliqpoint.store or qliqpoint.fashion, and link is qliqpoint.fashion or latestcollection.fashion
    if (
      /qliqpoint\.(store|fashion)/.test(url) &&
      /\.(qliqpoint|latestcollection)\.fashion/.test(link)
    ) {
      if (this.isLocalhost(url)) {
        // Localhost logic
        return link.replace(
          /(http?:\/\/)([^./]+\.)?(qliqpoint|latestcollection)\.fashion/,
          (_full: string, proto: string, sub: string, domain: string) =>
            `${proto}${sub ? sub : ''}qliqpoint.lc`,
        );
      }
      if (!subdomain) {
        return link;
      }

      // Handle both www.qliqpoint.fashion, www.latestcollection.fashion, and plain
      return link.replace(
        /:\/\/(www\.)?(qliqpoint|latestcollection)\.fashion/,
        `://${subdomain}.$2.fashion`,
      );
    }
    // For other domains, return the link unchanged
    return link;
  }

  /**
   * Checks if the given url is localhost
   * @param {string} url
   * @return {boolean}
   */
  private isLocalhost(url: string): boolean {
    return /localhost|127\.0\.0\.1|0\.0\.0\.0/.test(url);
  }
}
